// RFC-0008 T4: 单 deck 详情 — SVG 渲染 + 翻页 + 下载 PPTX
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getDeck, deleteDeck, type DeckDetail } from '../lib/api';
import { useUser } from '../components/user-provider';
import { getTemplateName } from '../lib/templates';

export function DeckRoute() {
  const { deck_id } = useParams<{ deck_id: string }>();
  const { user } = useUser();
  const appUserId = user?.userId ?? null;
  const navigate = useNavigate();

  const [deck, setDeck] = useState<DeckDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeIdx, setActiveIdx] = useState(1);
  const [toast, setToast] = useState<string | null>(null);

  const fetchDeck = useCallback(async () => {
    if (!appUserId || !deck_id) return;
    try {
      const res = await getDeck(appUserId, deck_id);
      setDeck(res);
      if (res.pages.length > 0) setActiveIdx(res.pages[0].index);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [appUserId, deck_id]);

  useEffect(() => {
    void fetchDeck();
  }, [fetchDeck]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  }

  // 键盘翻页
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!deck) return;
      const pages = deck.pages;
      if (pages.length === 0) return;
      const curOrdinal = pages.findIndex((p) => p.index === activeIdx);
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
        if (curOrdinal < pages.length - 1) {
          e.preventDefault();
          setActiveIdx(pages[curOrdinal + 1].index);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        if (curOrdinal > 0) {
          e.preventDefault();
          setActiveIdx(pages[curOrdinal - 1].index);
        }
      } else if (e.key === 'Escape') {
        navigate('/');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deck, activeIdx, navigate]);

  async function handleDelete() {
    if (!deck || !appUserId) return;
    if (!confirm(`确定要删除「${deck.title}」吗？`)) return;
    try {
      await deleteDeck(appUserId, deck.deck_id);
      navigate('/');
    } catch (e) {
      showToast(`删除失败：${(e as Error).message}`);
    }
  }

  async function handleShare() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('链接已复制');
    } catch {
      showToast('复制失败');
    }
  }

  if (error) {
    return (
      <div className="page">
        <header className="page__header">
          <Link to="/" className="btn btn--ghost">
            ← 返回
          </Link>
        </header>
        <main className="page__body">
          <div className="center-card">
            <h2>加载失败</h2>
            <p>{error}</p>
          </div>
        </main>
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="page">
        <header className="page__header">
          <Link to="/" className="btn btn--ghost">
            ← 返回
          </Link>
        </header>
        <main className="page__body">
          <p>加载中…</p>
        </main>
      </div>
    );
  }

  const activePage = deck.pages.find((p) => p.index === activeIdx);

  return (
    <div className="page page--fit">
      <header className="page__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/" className="btn btn--ghost btn--sm">
            ← 返回
          </Link>
          <h1 className="page__title">{deck.title}</h1>
          <span className="template-chip">{getTemplateName(deck.template)}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={handleShare}>
            分享链接
          </button>
          <a
            className="btn btn--primary"
            href={deck.file_url}
            download={`${deck.deck_id}.pptx`}
          >
            下载 PPTX
          </a>
          <button className="btn btn--danger" onClick={handleDelete}>
            删除
          </button>
        </div>
      </header>

      <main className="page__body">
        <div className="viewer">
          <aside className="viewer__thumbs">
            {deck.pages.map((p) => (
              <button
                key={p.index}
                className={
                  'viewer__thumb' +
                  (p.index === activeIdx ? ' viewer__thumb--active' : '')
                }
                onClick={() => setActiveIdx(p.index)}
                aria-label={`第 ${p.index} 页`}
              >
                <span className="viewer__thumb-num">{p.index}</span>
                <div dangerouslySetInnerHTML={{ __html: p.svg }} />
              </button>
            ))}
          </aside>

          <section className="viewer__canvas">
            <div className="viewer__canvas-inner">
              {activePage && (
                <div dangerouslySetInnerHTML={{ __html: activePage.svg }} />
              )}
            </div>
          </section>
        </div>

        <p className="page__hint">
          ← → 翻页　·　ESC 返回　·　要修改内容请下载 PPTX 在 PowerPoint / Keynote / WPS 中编辑
        </p>
      </main>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
